// ============================================================
// FONADHOO CRM v3.0 - UTILITY FUNCTIONS
// Helper functions used across components
// ============================================================

// ══════════════════════════════════════════════════════════
// DATE & TIME UTILITIES
// ══════════════════════════════════════════════════════════

export function formatDate(dateString) {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDateTime(dateString) {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRelativeTime(dateString) {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

export function daysSince(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

export function daysUntil(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date - now;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

export function isOverdue(dateString) {
  if (!dateString) return false;
  const date = new Date(dateString);
  const now = new Date();
  return date < now;
}

export function isSoon(dateString, daysThreshold = 3) {
  const days = daysUntil(dateString);
  return days !== null && days >= 0 && days <= daysThreshold;
}

// ══════════════════════════════════════════════════════════
// NUMBER & PERCENTAGE UTILITIES
// ══════════════════════════════════════════════════════════

export function formatNumber(num) {
  if (num === null || num === undefined) return '—';
  return num.toLocaleString();
}

export function formatPercent(value, total) {
  if (!total || total === 0) return '0%';
  const percent = (value / total) * 100;
  return `${percent.toFixed(1)}%`;
}

export function calculatePercentage(value, total) {
  if (!total || total === 0) return 0;
  return Math.round((value / total) * 100);
}

// ══════════════════════════════════════════════════════════
// STRING UTILITIES
// ══════════════════════════════════════════════════════════

export function truncate(str, maxLength) {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + '…';
}

export function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(word => word[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function cleanPhoneNumber(phone) {
  if (!phone) return '';
  return phone.replace(/[^0-9+]/g, '');
}

export function formatPhoneNumber(phone) {
  if (!phone) return '—';
  const cleaned = cleanPhoneNumber(phone);
  if (cleaned.startsWith('+960')) {
    return cleaned.replace(/(\+960)(\d{3})(\d{4})/, '$1 $2-$3');
  }
  if (cleaned.length === 7) {
    return cleaned.replace(/(\d{3})(\d{4})/, '$1-$2');
  }
  return cleaned;
}

// ══════════════════════════════════════════════════════════
// VOTER UTILITIES
// ══════════════════════════════════════════════════════════

export function calculateAge(dob) {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

export function isEligibleVoter(dob, asOfDate = new Date()) {
  const age = calculateAge(dob);
  return age !== null && age >= 18;
}

export function getAgeGroup(age) {
  if (age < 18) return 'Under 18';
  if (age <= 25) return '18-25';
  if (age <= 40) return '26-40';
  if (age <= 60) return '41-60';
  return '60+';
}

export function getVoterDisplayName(voter) {
  if (!voter) return 'Unknown';
  return voter.name || `Voter #${voter.id}`;
}

// ══════════════════════════════════════════════════════════
// SUPPORT LEVEL UTILITIES
// ══════════════════════════════════════════════════════════

export function getSupportScore(support) {
  const scores = {
    'Strong Support': 5,
    'Leaning Support': 4,
    'Undecided': 3,
    'Leaning Against': 2,
    'Strong Against': 1,
  };
  return scores[support] || 0;
}

export function isSupportive(support) {
  return support === 'Strong Support' || support === 'Leaning Support';
}

export function isAgainst(support) {
  return support === 'Strong Against' || support === 'Leaning Against';
}

export function isMovable(support) {
  return support === 'Undecided' || support === 'Leaning Support' || support === 'Leaning Against';
}

// ══════════════════════════════════════════════════════════
// ANALYTICS UTILITIES
// ══════════════════════════════════════════════════════════

export function groupBy(array, key) {
  return array.reduce((result, item) => {
    const group = item[key] || 'Unknown';
    if (!result[group]) {
      result[group] = [];
    }
    result[group].push(item);
    return result;
  }, {});
}

export function countBy(array, key) {
  return array.reduce((result, item) => {
    const value = item[key] || 'Unknown';
    result[value] = (result[value] || 0) + 1;
    return result;
  }, {});
}

export function filterByDateRange(array, dateField, startDate, endDate) {
  return array.filter(item => {
    const itemDate = new Date(item[dateField]);
    return itemDate >= startDate && itemDate <= endDate;
  });
}

export function calculateConversionRate(converted, total) {
  if (!total || total === 0) return 0;
  return Math.round((converted / total) * 100);
}

// ══════════════════════════════════════════════════════════
// TERRITORY UTILITIES
// ══════════════════════════════════════════════════════════

export function canAccessVoter(userRole, userTerritories, voterIsland, voterWard) {
  const fullAccessRoles = [
    'super_admin', 'campaign_manager', 'candidate',
    'atoll_coordinator', 'city_manager', 'island_manager',
    'outreach_manager', 'event_manager',
  ];
  if (fullAccessRoles.includes(userRole)) return true;

  // Patch agents can only access their assigned territories
  if (userRole === 'patch_agent' && userTerritories) {
    return userTerritories.some(
      t => t.island === voterIsland && t.ward === voterWard && t.is_active
    );
  }

  return false;
}

export function getAssignedVoterCount(territories, allVoters) {
  if (!territories || territories.length === 0) return 0;
  
  return allVoters.filter(voter => 
    territories.some(
      t => t.island === voter.island && t.ward === voter.ward && t.is_active
    )
  ).length;
}

// ══════════════════════════════════════════════════════════
// PERSUADABILITY SCORING
// ══════════════════════════════════════════════════════════

export function calculatePersuadabilityScore(voter, familySupport, events, opponentStrength) {
  let score = 0;
  
  // Base score from current support (max 50 points)
  if (voter.support === 'Undecided') score += 50;
  else if (voter.support === 'Leaning Against') score += 30;
  else if (voter.support === 'Leaning Support') score += 20;
  else if (voter.support === 'Strong Against') score += 5;
  else if (voter.support === 'Strong Support') score += 0; // Already converted
  
  // Family connections (max 30 points)
  if (familySupport >= 3) score += 30;
  else if (familySupport >= 2) score += 20;
  else if (familySupport >= 1) score += 10;
  
  // Engagement (max 15 points)
  if (voter.contact_count >= 3) score += 15;
  else if (voter.contact_count >= 2) score += 10;
  else if (voter.contact_count >= 1) score += 5;
  
  // Event attendance (max 10 points)
  if (events >= 2) score += 10;
  else if (events >= 1) score += 5;
  
  // Opponent strength (max 10 points - easier if weak opponent)
  if (opponentStrength === 'Weak') score += 10;
  else if (opponentStrength === 'Moderate') score += 5;
  
  // Likelihood to vote (max 10 points)
  if (voter.likelihood_to_vote === 'Will definitely vote') score += 10;
  else if (voter.likelihood_to_vote === 'Might vote') score += 5;
  
  return Math.min(100, score); // Cap at 100
}

export function getPersuadabilityTier(score) {
  if (score >= 80) return { tier: 'Hot', emoji: '🔥', color: '#ef4444' };
  if (score >= 50) return { tier: 'Warm', emoji: '🟡', color: '#f59e0b' };
  if (score >= 20) return { tier: 'Cool', emoji: '🔵', color: '#3b82f6' };
  return { tier: 'Cold', emoji: '❄️', color: 'var(--text-3)' };
}

// ══════════════════════════════════════════════════════════
// GOTV UTILITIES
// ══════════════════════════════════════════════════════════
// `committedIds` is a Set<voter_id> identifying voters who have an
// outreach row with outcome='Commitment Made'. We don't read the
// legacy `voter.vote_commitment` field anymore — no UI ever sets it,
// so it would always be falsy, making these functions misclassify
// every supporter as "high priority." Callers must pass the Set
// (typically built once from a single Supabase query in their parent
// component and threaded down).

export function getGotvPriority(voter, committedIds) {
  // High priority: Strong supporters who haven't committed or need transport
  if (isSupportive(voter.support)) {
    if (!committedIds || !committedIds.has(voter.id)) return 'High';
    if (voter.needs_transport) return 'High';
    if (voter.likelihood_to_vote === 'Might vote') return 'Medium';
    return 'Low'; // Committed and will vote
  }
  return 'None'; // Not a supporter
}

export function needsGotvAttention(voter, committedIds) {
  return (
    isSupportive(voter.support) &&
    ((!committedIds || !committedIds.has(voter.id)) ||
     voter.needs_transport ||
     voter.likelihood_to_vote !== 'Will definitely vote')
  );
}

// ══════════════════════════════════════════════════════════
// FAMILY NAME DERIVATION
// ══════════════════════════════════════════════════════════
// Compute a human-readable family name from its members. The DB column
// `families.family_name` is ignored entirely — auto-derivation produces
// consistent results across the app and stays in sync as voters are
// renamed.
//
// All derived names use the "Family of …" prefix so the dropdown reads
// uniformly and alphabetical sort places every family in one section.
//
// Rules:
//   • 0 members              → "(empty family)"
//   • 2 root members linked
//     by spouse               → "Family of FirstFullName & SecondFullName"
//   • 1 root member           → "Family of FullName"
//   • Many roots, no spouse   → "Family of FirstRootFullName"
//
// "Roots" are members whose `parent_ids` is missing or empty — i.e. the
// top of the tree. If we only ever have one root, that's a single
// founder. If we have two roots that point to each other via
// `spouse_id` (in either direction), that's the founding couple.
//
// Inputs:
//   family   — the family row (id, etc.)
//   members  — array of family_members for this family. Must include
//              parent_ids and spouse_id fields.
//   votersById — Map<id, voter> OR plain object {id: voter} — used to
//                resolve voter_id to a name. Pass either shape; we
//                detect at runtime.

export function deriveFamilyName(family, members, votersById) {
  if (!members || members.length === 0) return '(empty family)';

  const lookupVoter = (id) => {
    if (!votersById) return null;
    if (typeof votersById.get === 'function') return votersById.get(id) || votersById.get(String(id));
    // Try the raw key first, then a stringified version. Family members'
    // voter_id can come back as either a string or a number from Supabase
    // depending on column type, so we cover both.
    return votersById[id] || votersById[String(id)] || null;
  };
  const nameOf = (member) => {
    if (!member) return null;
    const v = lookupVoter(member.voter_id);
    return (v && v.name) ? v.name : null;
  };

  // Find roots: members with no parents.
  const roots = members.filter(m => !m.parent_ids || m.parent_ids.length === 0);

  if (roots.length === 0) {
    // Pathological case — every member has parents. Fall back to first member.
    const n = nameOf(members[0]);
    return n ? `Family of ${n}` : '(unnamed family)';
  }

  if (roots.length === 1) {
    const n = nameOf(roots[0]);
    return n ? `Family of ${n}` : '(unnamed family)';
  }

  // Multiple roots — find a spouse-linked pair. spouse_id is one-directional
  // in the DB so check both directions. Ordering uses member.id ascending
  // for stability (matches how FamilyTree lays them out left-to-right).
  const sortedRoots = [...roots].sort((a, b) => {
    if (a.id < b.id) return -1;
    if (a.id > b.id) return 1;
    return 0;
  });

  for (const r of sortedRoots) {
    const partner = sortedRoots.find(s =>
      s !== r && (
        (r.spouse_id && s.id === r.spouse_id) ||
        (s.spouse_id && r.id === s.spouse_id)
      )
    );
    if (partner) {
      const n1 = nameOf(r);
      const n2 = nameOf(partner);
      if (n1 && n2) return `Family of ${n1} & ${n2}`;
      if (n1) return `Family of ${n1}`;
      if (n2) return `Family of ${n2}`;
      return '(unnamed family)';
    }
  }

  // Multiple unconnected roots — go with the first one.
  const n = nameOf(sortedRoots[0]);
  return n ? `Family of ${n}` : '(unnamed family)';
}

// ══════════════════════════════════════════════════════════
// VALIDATION UTILITIES
// ══════════════════════════════════════════════════════════

export function isValidEmail(email) {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
}

export function isValidPhone(phone) {
  const cleaned = cleanPhoneNumber(phone);
  return cleaned.length >= 7 && cleaned.length <= 15;
}

export function isValidIdNumber(idNumber) {
  // Maldivian ID format: A123456
  return /^[A-Z]\d{6}$/.test(idNumber);
}

// ══════════════════════════════════════════════════════════
// EXPORT UTILITIES
// ══════════════════════════════════════════════════════════

export function exportToCSV(data, filename) {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }
  
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' && value.includes(',')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
}

// ══════════════════════════════════════════════════════════
// LOCAL STORAGE UTILITIES
// ══════════════════════════════════════════════════════════

export function saveToLocalStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    console.error('Error saving to localStorage:', e);
    return false;
  }
}

export function loadFromLocalStorage(key, defaultValue = null) {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (e) {
    console.error('Error loading from localStorage:', e);
    return defaultValue;
  }
}

export function removeFromLocalStorage(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    console.error('Error removing from localStorage:', e);
    return false;
  }
}

// ══════════════════════════════════════════════════════════
// DEBOUNCE & THROTTLE
// ══════════════════════════════════════════════════════════

export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function throttle(func, limit) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// ══════════════════════════════════════════════════════════
// EXPORT ALL
// ══════════════════════════════════════════════════════════

export default {
  formatDate,
  formatDateTime,
  formatRelativeTime,
  daysSince,
  daysUntil,
  isOverdue,
  isSoon,
  formatNumber,
  formatPercent,
  calculatePercentage,
  truncate,
  capitalize,
  getInitials,
  cleanPhoneNumber,
  formatPhoneNumber,
  calculateAge,
  isEligibleVoter,
  getAgeGroup,
  getVoterDisplayName,
  getSupportScore,
  isSupportive,
  isAgainst,
  isMovable,
  groupBy,
  countBy,
  filterByDateRange,
  calculateConversionRate,
  canAccessVoter,
  getAssignedVoterCount,
  calculatePersuadabilityScore,
  getPersuadabilityTier,
  getGotvPriority,
  needsGotvAttention,
  deriveFamilyName,
  isValidEmail,
  isValidPhone,
  isValidIdNumber,
  exportToCSV,
  saveToLocalStorage,
  loadFromLocalStorage,
  removeFromLocalStorage,
  debounce,
  throttle,
};
